import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { FetchCatalogItemsParams } from '@/redux/modules/catalog/items/catalogItems.types';
import { getDeployment, getUrlFromPathname } from '@/redux/modules/config';
import { fetchItemSummariesByCatalogId } from '@/redux/modules/item/summary/itemSummary.api';
import { FetchItemsSummariesResponse } from '@/types/item/ItemSummary';
import { isClientSide } from '@/utils/amplitude/analytics/core';
import { PriceResultsMatch, SearchCompletedTrigger, SearchContext } from '@/utils/amplitude/analytics/core.types';
import {
    trackSearchCompletedAnalytics,
    TrackSearchCompletedSearchType,
} from '@/utils/amplitude/analytics/search/trackSearchCompleted.analytics';
import { trackSearchRefinedAnalytics } from '@/utils/amplitude/analytics/search/trackSearchRefined.analytics';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

export const fetchCatalogItemSummaries = createAsyncThunk<
    FetchItemsSummariesResponse['payload'],
    FetchCatalogItemsParams
>(
    'la/ui/catalogItems/fetchCatalogItemSummaries',
    async ({ catalogId, filterAction, query, shouldUseElasticOnCatalogPagesExperiment }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const { payload } = await fetchItemSummariesByCatalogId({
            authToken,
            catalogId,
            deployment,
            query,
            useATGSearch: shouldUseElasticOnCatalogPagesExperiment,
        });

        const sanitizedKeyword = typeof query.keyword === 'string' ? query.keyword.trim() : '';
        const hasAlphanumericContent = sanitizedKeyword.length > 0 && /[a-zA-Z0-9]/.test(sanitizedKeyword);

        if (isClientSide() && hasAlphanumericContent) {
            const pathname = window.location.pathname;
            const normalizedSearchQuery = sanitizedKeyword.toLowerCase();
            const page = typeof query.page === 'number' ? query.page : 1;
            const numberOfSearchResults = payload.recordCount || payload.items.length || 0;
            const pageSize =
                query.pageSize === 'all'
                    ? payload.items.length
                    : typeof query.pageSize === 'number'
                      ? query.pageSize
                      : payload.items.length;

            if (filterAction) {
                trackSearchRefinedAnalytics({
                    lotIds: payload.items.map((i) => i.itemId).join(','),
                    normalizedSearchQuery,
                    numberOfSearchResults,
                    page,
                    pageName: getAmplitudePageNameFromPath(pathname),
                    pageSize,
                    searchQuery: sanitizedKeyword,
                    sort: query.sort,
                    url: getUrlFromPathname(state, pathname),
                });
            } else {
                trackSearchCompletedAnalytics({
                    normalizedSearchQuery,
                    numberOfSearchResults,
                    page,
                    pageName: getAmplitudePageNameFromPath(pathname),
                    pageSize,
                    priceResultsMatch: PriceResultsMatch.No,
                    searchContext: SearchContext.UpcomingItems,
                    searchQuery: sanitizedKeyword,
                    searchSuggestionUsed: false,
                    searchType: TrackSearchCompletedSearchType.Keyword,
                    trigger: SearchCompletedTrigger.CatalogSearch,
                    url: getUrlFromPathname(state, pathname),
                });
            }
        }

        return payload;
    }
);
